import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../@types/redux';
import { formatDateTime } from '../../services/appFunctionsService';
import { generalShowErrorSnackbar } from '../../store/general/Functions';
import Screen from '../../components/Screen';
import { CustomTooltip, PillButton, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../@types/other';
import { syncMasterData } from '../../services/masterDataSyncService';
import ColdStorageHttpService from '../../services/http/coldStorage/coldStorageHttpService';
import { IStorageUnit } from '../../@types/model/masterData/storageUnit/storageUnit';
import { usePrevious } from '../../hooks/usePrevious';
import lodash from 'lodash';
import StorageUnitHttpService from '../../services/http/masterData/storageUnitHttpService';
import SiteHttpService from '../../services/http/masterData/siteHttpService';
import { dataSetSites, dataSetStorageUnits } from '../../store/masterData/Actions';
import { CustomTable } from '../../components/table/CustomTableWrapper';
import ColdStorageModelHelper from '../../@types/model/coldStorage/coldStorageModelHelper';
import ColdStorageScheduleDialog from './components/ColdStorageScheduleDialog';
import ColdStorageEndScheduleDialog from './components/ColdStorageEndScheduleDialog';
import ColdStorageSummary from './ColdStorageSummary';
import { IColdStorageFormValues } from '../../@types/model/coldStorage/coldStorageFormValues';
import moment from 'moment';
import { IColdStorage } from '../../@types/model/coldStorage/coldStorage';

const ColdStorageScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const selectedSiteIds = useAppSelector(x => x.data.selectedSiteIds);
    const storageUnits = useAppSelector(x => x.masterData.storageUnits);
    const sites = useAppSelector(state => state.masterData.sites);

    const [coldStorageList, setColdStorageList] = useState<Array<IColdStorage>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState<boolean>(false);
    const [isEndSessionDialogOpen, setIsEndSessionDialogOpen] = useState<boolean>(false);
    const [isSummaryOpen, setIsSummaryOpen] = useState<boolean>(false);
    const [selectedStorageUnit, setSelectedStorageUnit] = useState<IStorageUnit | null>(null);

    const prevSelectedSiteIds = usePrevious(selectedSiteIds);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (!lodash.isEqual(prevSelectedSiteIds, selectedSiteIds)) {
            fetchData();
        }
    }, [selectedSiteIds]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/
    
    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        setIsLoading(true);

        if (isIndexedDBAvailable) {
            await syncMasterData(false);

            await loadLatestColdStorageData();

            setIsLoading(false);
        } else {
            try {
                const [storageUnitRes, siteRes] = await Promise.all([
                    StorageUnitHttpService.getStorageUnitData(),
                    SiteHttpService.getSiteData(),
                ]);

                dispatch(dataSetSites(siteRes.data));
                dispatch(dataSetStorageUnits(storageUnitRes.data));

                const res = await ColdStorageHttpService.GetLatest(storageUnitRes.data.filter(x => x.isActive).map(x => x.id));
                setColdStorageList(res.data);
            } catch (error : unknown) {
                const apiError = error as IApiError;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading cold storage data.');
            } finally {
                setIsLoading(false);
            }
        }
    };
    
    const loadLatestColdStorageData = async () => {
        try {
            const res = await ColdStorageHttpService.GetLatest(filteredStorageUnits.map(x => x.id));
            setColdStorageList(res.data);
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading cold storage data.');
        }
    };

    const scheduleNewSession = async (values : IColdStorageFormValues) => {
        if (!selectedStorageUnit) return;

        try {
            setIsLoading(true);

            const upsert = ColdStorageModelHelper.createUpsert(values);

            await ColdStorageHttpService.upsert(upsert);

            loadLatestColdStorageData();

            closeScheduleSessionDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError | null;
            generalShowErrorSnackbar(apiError?.data.Message ?? 'An error occurred while creating new cold storage session.');
        }
        finally {
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const getSiteDescription = (siteId : number) => {
        const site = sites.find(x => x.id === siteId);
        return site?.description ?? '';
    };

    const onScheduleSessionClick = (row : IStorageUnit) => {
        setSelectedStorageUnit(row);
        setIsScheduleDialogOpen(true);
    };

    const closeScheduleSessionDialog = () => {
        setSelectedStorageUnit(null);
        setIsScheduleDialogOpen(false);
    };

    const onEndSessionClick = (row : IStorageUnit) => {
        setSelectedStorageUnit(row);
        setIsEndSessionDialogOpen(true);
    };

    const closeEndSessionDialog = (refresh : boolean) => {
        setSelectedStorageUnit(null);
        setIsEndSessionDialogOpen(false);

        if (refresh) {
            loadLatestColdStorageData();
        }
    };

    const onSummaryClick = (row : IStorageUnit) => {
        setSelectedStorageUnit(row);
        setIsSummaryOpen(true);
    };

    const closeSummary = () => {
        setSelectedStorageUnit(null);
        setIsSummaryOpen(false);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        return ColdStorageModelHelper.createFormValues(null, storageUnits, selectedStorageUnit);
    }, [selectedStorageUnit, isScheduleDialogOpen, storageUnits]);

    const filteredStorageUnits = useMemo(() => {
        return storageUnits.filter(x => x.isActive && selectedSiteIds?.includes(x.siteId));
    }, [selectedSiteIds, storageUnits]);

    const selectedUnitColdStorageSession = useMemo<IColdStorage | null>(() => {
        if (!selectedStorageUnit) return null;

        return coldStorageList.find(x => x.storageUnitId === selectedStorageUnit.id && x.isActive) ?? null;
    }, [selectedStorageUnit, coldStorageList]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    const renderCreateSessionButton = (row : IStorageUnit) : JSX.Element => {
        return (
            <CustomTooltip title={'Schedule a new session for this storage unit'}>
                <PillButton
                    text={'Start Session'}
                    onClick={() : void => onScheduleSessionClick(row)}
                    disabled={isLoading}
                    color={'secondary'}
                    className={'mnw100 h35'}
                />
            </CustomTooltip>
        );
    };

    const renderEndSessionButton = (row : IStorageUnit) : JSX.Element => {
        const latestColdStorageSession = coldStorageList.find(x => x.storageUnitId === row.id && x.isActive);

        const disabled = !latestColdStorageSession
            || (!!latestColdStorageSession.endedOn && moment.utc(latestColdStorageSession.endedOn).local().isBefore(moment().local()));

        return (
            <CustomTooltip title={disabled
                ? 'No current session to end'
                : 'End the current session for this storage unit'
            }
            >
                <PillButton
                    text={'End Session'}
                    onClick={() : void => onEndSessionClick(row)}
                    disabled={isLoading || disabled}
                    color={'secondary'}
                    className={'mnw100 h35'}
                />
            </CustomTooltip>
        );
    };

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IStorageUnit>
                        
                        enableDetails
                        detailFunction={onSummaryClick}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Id', field: 'id', enableFiltering: true, enableSorting: true },
                            { title: '', field: 'id', width: 160,
                                containerComponent: (row) => renderCreateSessionButton(row as IStorageUnit),
                            },
                            { title: '', field: 'guid', width: 150,
                                containerComponent: (row) => renderEndSessionButton(row as IStorageUnit),
                            },
                            { title: 'Site', field: 'siteId', formatFunction: getSiteDescription, width: 200, enableFiltering: true, enableSorting: true },
                            { title: 'Code', field: 'code', enableFiltering: true, flex: 1, enableSorting: true },
                            { title: 'Description', field: 'description', width: 300, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, flex: 1, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, flex: 1, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, flex: 1, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, flex: 1, enableFiltering: true, enableSorting: true },
                        ]}
                        rows={filteredStorageUnits}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isScheduleDialogOpen &&
                <ProjectDialog
                    title={'Schedule New Cold Storage Session'}
                    isLoadingCircular={isLoading}
                    isOpen={isScheduleDialogOpen}
                    onClose={closeScheduleSessionDialog}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <ColdStorageScheduleDialog
                        initialValues={initialValues}
                        onSubmit={scheduleNewSession}
                        onClose={closeScheduleSessionDialog}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            {isSummaryOpen && selectedStorageUnit &&
                <ProjectDialog
                    title={'Cold Storage Summary'}
                    isLoadingCircular={isLoading}
                    isOpen={isSummaryOpen}
                    onClose={closeSummary}
                    fullScreen
                    fullWidth
                >
                    <ColdStorageSummary
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        selectedStorageUnit={selectedStorageUnit}
                    />
                </ProjectDialog>
            }
            {isEndSessionDialogOpen && selectedStorageUnit &&
                <ColdStorageEndScheduleDialog
                    isOpen={isEndSessionDialogOpen}
                    isLoading={isLoading}
                    selectedStorageUnit={selectedStorageUnit}
                    latestColdStorageSession={selectedUnitColdStorageSession}
                    setIsLoading={setIsLoading}
                    onClose={closeEndSessionDialog}
                />
            }
        </Screen>
    );
};

export default ColdStorageScreen;
